import type { ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, Copy, HelpCircle } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

export const TooltipProvider = TooltipPrimitive.Provider;

interface TooltipProps {
  content: ReactNode;
  children?: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  delayDuration?: number;
  sideOffset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Permite interagir com o conteúdo (copiar, clicar). */
  interactive?: boolean;
  /** Texto único com botão de copiar no rodapé. */
  copyText?: string;
}

const contentStyles = cn(
  'glass-elevated z-50 max-w-sm rounded-xl border border-border/50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground shadow-glass-lg backdrop-blur-md',
  'animate-in fade-in-0 zoom-in-95 duration-200',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-150',
  'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
  'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
);

function TooltipCopyFooter({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copiado!', { duration: 1800 });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  }, [text]);

  return (
    <div className="mt-2 flex justify-end border-t border-border/40 pt-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="h-7 gap-1.5 text-xs"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          void handleCopy();
        }}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        Copiar
      </Button>
    </div>
  );
}

/** Tooltip Glass (Radix/shadcn). Sem `children`, renderiza ícone de ajuda. */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  className,
  delayDuration,
  sideOffset = 8,
  open,
  onOpenChange,
  interactive = false,
  copyText,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root
      delayDuration={delayDuration}
      open={open}
      onOpenChange={onOpenChange}
    >
      <TooltipPrimitive.Trigger asChild>
        {children ?? (
          <button
            type="button"
            className="inline-grid place-items-center text-muted-foreground/70 transition-colors hover:text-foreground"
            aria-label="Ajuda"
          >
            <HelpCircle className="size-4" />
          </button>
        )}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(contentStyles, className)}
          onPointerDown={(e) => interactive && e.stopPropagation()}
        >
          <div onClick={(e) => interactive && e.stopPropagation()}>{content}</div>
          {copyText ? <TooltipCopyFooter text={copyText} /> : null}
          <TooltipPrimitive.Arrow className="fill-[hsl(var(--glass-bg))] drop-shadow-sm" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export { TooltipPrimitive };
